import * as React from 'react';
import { useTheme } from '@emotion/react';
import { Breadcrumb, Breadcrumbs, Inline, useBreakpoint } from '@resi-media/resi-ui';
import { Link, useParams } from 'react-router';
import { Skeleton } from '@sites/components/Skeleton';
import { PAGE_TYPES, SEARCH } from '@sites/constants/generic';
import { useLivePlayer } from '@sites/contexts/livePlayerContext';
import { useQueryParams } from '@sites/contexts/queryContext';
import { useSearch } from '@sites/contexts/searchContext';
import { useHeaderVisibility } from '@sites/hooks/useHeaderVisibility';
import * as globalStyles from '@sites/styles/global';
import type { Playlists, Shared, Videos } from '@sites/types';
import { usePrefix } from '@studio/hooks/use-prefix';
import { SearchInput } from '../SearchInput';

type _Props = {
  currentPlaylist?: Playlists.Get.Playlist;
  isLoading: boolean;
  variant?: Shared.Video.Variants;
  video?: Playlists.Derived.Video | Videos.Get.Video;
};

const VideoHeader = ({ currentPlaylist, isLoading, variant = PAGE_TYPES.DEFAULT, video: currentVideo }: _Props) => {
  const { commonT } = usePrefix('components:', 'breadcrumbs');
  const theme = useTheme();
  const { customerSite, playlistId = '' } = useParams<'customerSite' | 'playlistId'>();
  const mediaQuery = useBreakpoint();
  const isSearch = variant === PAGE_TYPES.SEARCH;
  const { isSearchInputExpanded, isSearchPopoverOpen, search } = useSearch();
  const { isEmbed, isTag } = useQueryParams();
  const isMobileAndSearchOpen = !mediaQuery.lg && isSearchPopoverOpen;
  const isMobileAndSearchExpanded = !mediaQuery.lg && isSearchInputExpanded;
  const isHeaderVisible = useHeaderVisibility();
  const { videoName } = useLivePlayer();

  const breadcrumbs = React.useMemo(() => {
    return (
      <Breadcrumbs colorVariant="heading" dataTestId="video-header-breadcrumbs">
        <Skeleton height="1rem" isLoading={isLoading} width="5vw">
          <Breadcrumb as={Link} to={`/${customerSite}`} weightVariant="semiBold">
            {commonT('home')}
          </Breadcrumb>
        </Skeleton>
        <Skeleton height="1rem" isLoading={isLoading} width={isSearch ? '5vw' : '10vw'}>
          {isSearch && (
            <Breadcrumb
              as={Link}
              dataTestId="search-results-breadcrumb"
              to={`/${customerSite}/${SEARCH}?q=${search}${isTag ? '&isTag=true' : ''}`}
              weightVariant="semiBold"
            >
              {commonT('searchResults')}
            </Breadcrumb>
          )}
          {variant === PAGE_TYPES.DEFAULT && (
            <Breadcrumb
              as="a"
              {...(currentPlaylist && { as: Link, to: `/${customerSite}/explore/${playlistId}` })}
              dataTestId="playlist-name-breadcrumb"
              weightVariant="semiBold"
            >
              {currentPlaylist?.name ?? commonT('notFound')}
            </Breadcrumb>
          )}
        </Skeleton>
        <Skeleton height="1rem" isLoading={isLoading} width="10vw">
          {(currentPlaylist || isSearch) && (
            <Breadcrumb dataTestId="video-name-breadcrumb">{currentVideo?.title ?? commonT('notFound')}</Breadcrumb>
          )}
          {variant === PAGE_TYPES.LIVE && <Breadcrumb dataTestId="live-video-name-breadcrumb">{videoName}</Breadcrumb>}
        </Skeleton>
      </Breadcrumbs>
    );
  }, [
    commonT,
    currentPlaylist,
    currentVideo?.title,
    customerSite,
    isLoading,
    isSearch,
    isTag,
    playlistId,
    search,
    variant,
    videoName,
  ]);

  return isEmbed ? (
    <Inline
      css={(theme) => globalStyles.searchInputWrapper(theme, isHeaderVisible)}
      dataTestId="video-header-embed-container"
      height="auto"
      px={{ xs: theme.spacing.x5, lg: theme.spacing.x12 }}
      py={theme.spacing.x5}
      style={{
        ...(isMobileAndSearchOpen
          ? {
              background: theme.palette.surface.canvas,
            }
          : {
              background: theme.palette.contrastInverse,
            }),
      }}
      width="scale"
      zIndex={theme.zIndex.tooltip + 1}
    >
      <Inline alignItems="center" justifyContent="space-between" width="scale">
        {!isMobileAndSearchExpanded && breadcrumbs}
        <Inline justifyContent="flex-end" width={{ xs: isSearchInputExpanded ? 'scale' : 'fit', lg: '385px' }}>
          <SearchInput />
        </Inline>
      </Inline>
    </Inline>
  ) : (
    breadcrumbs
  );
};

VideoHeader.displayName = 'VideoHeader';

export default VideoHeader;
